import { useState, useEffect, useRef, useMemo } from 'react';
import { Stethoscope, ChevronRight, ChevronLeft, CheckCircle, AlertTriangle, ClipboardCheck, RotateCcw, X, Globe } from 'lucide-react';
import { t, tOptions, tUI } from '@/utils/formTranslations';
import { isFieldVisible } from '@/utils/formConditions';
import { isCeremonyField } from '@/utils/intakeCeremony';
import axios from 'axios';
import { createKioskApi, KIOSK_TOKEN_KEY, KIOSK_REFRESH_KEY } from '../utils/kioskApi';
import SignaturePad from '@/components/ui/SignaturePad';
import { createKioskSubmission, confirmKioskSubmission } from '@/services/kioskSubmission';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Kiosk steps: search → (abn for medicare patients without one) →
// (insurance card capture if no recent card on file) → select forms →
// fill forms → done
const STEPS = { SEARCH: 'search', ABN: 'abn', INS_CARD: 'ins_card', SELECT_FORMS: 'select_forms', FILL: 'fill', DONE: 'done' };

// Draft persistence — iPad Safari aggressively kills background tabs to
// reclaim memory; without this, a patient who pauses (e.g., screen lock)
// returns to an empty form and may not notice before submitting. Bug
// caught 2026-06-10 (Librado Plaza + Kenny Le intake forms saved as
// completed but blank). Keyed by patient ID so each patient's draft is
// scoped and there's no chance of bleed-through between patients. Old
// drafts auto-expire after 4 hours.
const DRAFT_PREFIX = 'cervica_kiosk_draft_v1_';
const DRAFT_TTL_MS = 4 * 60 * 60 * 1000;

function loadKioskDraft(patientId) {
  if (!patientId) return null;
  try {
    const raw = localStorage.getItem(DRAFT_PREFIX + patientId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.savedAt || Date.now() - parsed.savedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(DRAFT_PREFIX + patientId);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveKioskDraft(patientId, payload) {
  if (!patientId) return;
  try {
    localStorage.setItem(DRAFT_PREFIX + patientId, JSON.stringify({ ...payload, savedAt: Date.now() }));
    return true;
  } catch {
    // Autosave remains best-effort; submission explicitly checks durability.
    return false;
  }
}

// Expired drafts were only removed when that same patient came back, so a
// shared iPad slowly filled its storage quota with other patients' answers
// and signatures until every save failed. Sweep them when the kiosk opens.
function pruneExpiredKioskDrafts() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key?.startsWith(DRAFT_PREFIX)) continue;
      let savedAt = 0;
      try { savedAt = JSON.parse(localStorage.getItem(key))?.savedAt || 0; } catch { /* unreadable: drop */ }
      if (Date.now() - savedAt > DRAFT_TTL_MS) localStorage.removeItem(key);
    }
  } catch { /* storage unavailable */ }
}

// Keep only non-answer recovery metadata beyond the four-hour draft lifetime.
// An unresolved receipt must not become a brand-new submission after reload.
const PENDING_PREFIX = 'cervica_kiosk_pending_v1_';
function readPendingMarker(patientId) {
  try { const marker = JSON.parse(localStorage.getItem(PENDING_PREFIX + patientId) || 'null');
    return marker?.patient_id === patientId && marker?.operation_id ? marker : null;
  } catch { return null; }
}
function writePendingMarker(operation) {
  try { localStorage.setItem(PENDING_PREFIX + operation.patient_id, JSON.stringify({
    operation_id:operation.operation_id, patient_id:operation.patient_id,
    form_count:operation.forms.length, outcome_count:operation.outcomes.length,
  })); return true; } catch { return false; }
}
function clearPendingMarker(patientId) { try { localStorage.removeItem(PENDING_PREFIX + patientId); } catch {} }

function clearKioskDraft(patientId) {
  if (!patientId) return;
  try { localStorage.removeItem(DRAFT_PREFIX + patientId); } catch {}
}

/**
 * Returns true if responses has at least one meaningful value. Mirrors
 * the server-side guard so the kiosk surfaces the error immediately
 * instead of round-tripping to a 400.
 */
function hasFilledValue(responses) {
  if (!responses || typeof responses !== 'object') return false;
  for (const v of Object.values(responses)) {
    if (v == null || v === '') continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    if (Array.isArray(v)) { if (v.length > 0) return true; continue; }
    if (typeof v === 'object') {
      if (Object.values(v).some((x) => x != null && x !== '')) return true;
      continue;
    }
    return true;
  }
  return false;
}

export default function KioskPage() {
  const [step, setStep] = useState(STEPS.SEARCH);
  useEffect(() => { pruneExpiredKioskDrafts(); }, []);

  // Self-updating kiosk: these iPads run as always-open tabs that nobody
  // reloads, so deployed fixes never reach them (ABN Option 3 sat broken on
  // the devices a full day after the fix shipped, 2026-07-08). Poll the
  // served index for a new bundle hash every 5 minutes and reload — but
  // ONLY on the idle search screen so we never interrupt a patient mid
  // check-in.
  const bundleHashRef = useRef(null);
  const stepRef = useRef(step);
  useEffect(() => { stepRef.current = step; }, [step]);
  useEffect(() => {
    async function checkVersion() {
      try {
        const html = await fetch('/', { cache: 'no-store' }).then((r) => r.text());
        const m = html.match(/assets\/index-[A-Za-z0-9_-]+\.js/);
        if (!m) return;
        if (bundleHashRef.current == null) { bundleHashRef.current = m[0]; return; }
        if (m[0] !== bundleHashRef.current && stepRef.current === STEPS.SEARCH) {
          window.location.reload();
        }
      } catch { /* offline — try again next tick */ }
    }
    checkVersion();
    const t = setInterval(checkVersion, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);
  // Swap in the kiosk manifest while this page is open. The app manifest's
  // start_url is /today, so "Add to Home Screen" from the kiosk page still
  // minted an icon that opened the staff app — the tablet showed "the same
  // as my desktop" (Damaris 2026-09-02, post domain-split re-setup). iOS
  // reads the manifest present in the DOM at add-time, so swapping here
  // makes the home-screen icon open /kiosk.
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prev = link?.getAttribute('href');
    if (link) link.setAttribute('href', '/kiosk-manifest.webmanifest');
    return () => { if (link && prev) link.setAttribute('href', prev); };
  }, []);
  const [authToken, setAuthToken] = useState(() => localStorage.getItem(KIOSK_TOKEN_KEY) || '');
  const [kioskSetup, setKioskSetup] = useState(false);
  // Language selector — persists for the session so a Spanish-speaking
  // patient who sets it doesn't have it reset when the kiosk auto-resets
  // between patients. Resets on app reload (no localStorage).
  const [lang, setLang] = useState('en');

  // Auth state — kiosk needs a staff login once, then stays logged in
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [practiceName, setPracticeName] = useState('');

  // Patient lookup (HIPAA-safe: last name + DOB, no list shown)
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');  // displayed as MM/DD/YYYY
  const [dobRaw, setDobRaw] = useState(''); // digits only for tracking
  const [lookupError, setLookupError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Form selection
  const [templates, setTemplates] = useState([]);
  // Templates the current patient has already submitted at least once.
  // These get hidden from the kiosk's select-forms list so we never
  // re-prompt patients to re-fill paperwork they've already done.
  const [completedTplIds, setCompletedTplIds] = useState(new Set());
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [outcomeInstruments, setOutcomeInstruments] = useState([]);
  const [selectedOutcomes, setSelectedOutcomes] = useState([]);

  // Form fill state
  const [currentForm, setCurrentForm] = useState(0);
  const [allResponses, setAllResponses] = useState({});
  const [signatures, setSignatures] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const submissionLock = useRef(false);
  const pendingSubmission = useRef(null);
  const pendingMarker = useRef(null);
  const [submissionRecovery, setSubmissionRecovery] = useState(false);
  const [submissionError, setSubmissionError] = useState('');

  // ABN signature state — populated only when a Medicare patient
  // arrives without an active ABN. The kiosk gates the rest of
  // check-in until a signature (Option 1 or 2) or a decline
  // (Option 3 → see front desk) is captured.
  const [abnOption, setAbnOption] = useState('1');
  const [abnSignatureData, setAbnSignatureData] = useState(null);
  const [abnSubmitting, setAbnSubmitting] = useState(false);
  const [abnError, setAbnError] = useState('');
  // A staff-pushed in-office ABN request (Send ABN -> In-office iPad). When
  // set, the ABN step completes THIS request (honoring the biller's draft),
  // works for any payer, and is independent of the Medicare auto-prompt.
  const [abnRequest, setAbnRequest] = useState(null);

  // Insurance card capture state. Only routed to when the patient
  // doesn't have a recent card on file (>90 days or never).
  const [cardFront, setCardFront] = useState(null);
  const [cardBack, setCardBack] = useState(null);
  const [cardSubmitting, setCardSubmitting] = useState(false);
  const [cardError, setCardError] = useState('');

  // Combined list of things to fill out
  const [formQueue, setFormQueue] = useState([]); // { type: 'form'|'outcome', data: template|instrument }
  // Whether this check-in needs the forms screen at all — false for
  // established patients with no staff-queued forms (they go straight
  // to "All Done" after ABN / insurance-card checks).
  const [formsStepNeeded, setFormsStepNeeded] = useState(true);

  const api = useMemo(() => createKioskApi(API_BASE), []);

  // Check if kiosk is set up
  useEffect(() => {
    if (authToken) {
      api.get('/auth/me').then((res) => {
        setPracticeName(res.data.user?.tenant_name || 'Our Practice');
        setKioskSetup(true);
        loadTemplates();
      }).catch(() => {
        localStorage.removeItem(KIOSK_TOKEN_KEY);
        localStorage.removeItem(KIOSK_REFRESH_KEY);
        setAuthToken('');
      });
    }
  }, [authToken]);

  async function handleLogin() {
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await axios.post(`${API_BASE}/auth/login`, { email: loginEmail, password: loginPassword });
      const token = res.data.token || res.data.accessToken;
      localStorage.setItem(KIOSK_TOKEN_KEY, token);
      if (res.data.refreshToken) localStorage.setItem(KIOSK_REFRESH_KEY, res.data.refreshToken);
      else localStorage.removeItem(KIOSK_REFRESH_KEY);
      setAuthToken(token);
    } catch (err) {
      setLoginError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  }

  async function loadTemplates(patientId) {
    // Pass for_patient when we know who's checking in so the server can
    // decorate each template with `required_by_assignment` (true when a
    // biller queued this specific form via the chart's Send Form
    // workflow). Kiosk then renders those above optional forms with a
    // Required badge.
    // Returns the fresh list — callers in the same tick must NOT read
    // the `templates` state after awaiting this (stale closure: the
    // decorated flags wouldn't be visible yet).
    try {
      const params = { active_only: 'true' };
      if (patientId) params.for_patient = patientId;
      const [formRes, outcomeRes] = await Promise.all([
        api.get('/forms/templates', { params }),
        api.get('/outcomes/instruments').catch(() => ({ data: { instruments: [] } })),
      ]);
      const fresh = formRes.data.templates || [];
      setTemplates(fresh);
      setOutcomeInstruments(outcomeRes.data.instruments || []);
      return fresh;
    } catch { return null; }
  }

  // Format DOB input: auto-insert slashes as user types digits
  function handleDobInput(e) {
    let val = e.target.value;
    // Allow only digits and slashes
    let digits = val.replace(/\D/g, '');
    // Cap at 8 digits (MMDDYYYY)
    digits = digits.slice(0, 8);
    setDobRaw(digits);
    // Format as MM/DD/YYYY
    let formatted = '';
    if (digits.length > 0) formatted += digits.slice(0, 2);
    if (digits.length > 2) formatted += '/' + digits.slice(2, 4);
    if (digits.length > 4) formatted += '/' + digits.slice(4, 8);
    setDob(formatted);
  }

  // Convert MM/DD/YYYY display to YYYY-MM-DD for DB comparison
  function dobToISO(display) {
    const parts = display.split('/');
    if (parts.length !== 3 || parts[2].length !== 4) return '';
    return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
  }

  // HIPAA-safe patient lookup: exact last name + DOB match on the server
  async function handlePatientLookup() {
    const dobISO = dobToISO(dob);
    if (!lastName.trim() || !dobISO) return;
    setLookupError('');
    setLookupLoading(true);
    try {
      const res = await api.get('/patients', {
        params: {
          last_name: lastName.trim(),
          date_of_birth: dobISO,
          limit: 5,
        },
      });
      const patients = res.data.patients || [];
      const match = patients[0] || null;

      if (match) {
        setSelectedPatient(match);

        // Refresh templates with for_patient now that we know who's
        // checking in. Server decorates kiosk/in_office queued
        // assignments with required_by_assignment=true so the form
        // selector can surface them prominently. Use the RETURNED list
        // below — reading `templates` state here would see the stale
        // undecorated copy (staff-pushed forms were being missed).
        const freshTpls = (await loadTemplates(match.id)) || templates;

        // Record the check-in (audit log + update appointment status)
        try {
          await api.post('/appointments/kiosk-checkin', { patient_id: match.id });
        } catch {}

        // Smart defaults: pre-select required forms ONLY if the patient
        // hasn't already submitted them. Without this check the kiosk
        // re-prompts patients who filled the intake at home via the
        // magic link, or who completed paperwork on a prior visit.
        //
        // We pull their existing submissions, build a set of template
        // IDs they've already done, and subtract from the required
        // list. If the API call fails, fall back to the prior behavior
        // (pre-select all required) — better to over-ask than under-ask.
        // Per-template "done" check honors ttl_days. A template with
        // ttl_days=null is one-time forever (Informed Consent). A
        // template with ttl_days=365 is annual — submission older than
        // 365 days no longer counts and the form re-prompts.
        const tplById = new Map(freshTpls.map((t) => [t.id, t]));
        const mostRecentByTpl = new Map(); // template_id → newest completed_at timestamp
        try {
          const subsRes = await api.get('/forms/submissions', {
            params: { patient_id: match.id, limit: 100 },
          });
          const subs = subsRes.data?.submissions || [];
          for (const s of subs) {
            const okStatus = s.status === 'submitted' || s.status === 'signed' || s.status === 'completed';
            if (!okStatus || !s.form_template_id) continue;
            // A signature-required form with no captured signature is not
            // done, whatever its status says — 360 legacy submissions are
            // "completed" unsigned, and counting them here made the kiosk
            // refuse to re-present the form the front desk queued for
            // exactly that reason (Marla Dixon's HIPAA, Anna 2026-07-30).
            // The server computes signature_status per submission.
            if (s.signature_status?.missing) continue;
            const ts = s.completed_at || s.created_at;
            if (!ts) continue;
            const existing = mostRecentByTpl.get(s.form_template_id);
            if (!existing || new Date(ts) > new Date(existing)) {
              mostRecentByTpl.set(s.form_template_id, ts);
            }
          }
        } catch {
          // Non-blocking — if we can't check, fall back to pre-selecting
          // nothing rather than re-asking everyone. Front desk catches.
        }

        const completedTemplateIds = new Set();
        const now = Date.now();
        for (const [tplId, ts] of mostRecentByTpl) {
          const tpl = tplById.get(tplId);
          const ttlDays = tpl?.ttl_days;
          if (ttlDays == null) {
            // No TTL → one submission is forever
            completedTemplateIds.add(tplId);
          } else {
            const ageDays = (now - new Date(ts).getTime()) / 86400000;
            if (ageDays < ttlDays) completedTemplateIds.add(tplId);
            // older submissions don't count → template falls through to "needs filling"
          }
        }
        setCompletedTplIds(completedTemplateIds);

        // Pre-select required forms ONLY for brand-new patients. A
        // patient is "new" when they have NO chart_number AND no CF
        // migration summary — meaning Cervica has nothing showing
        // they've been onboarded before. Returning patients (chart
        // number on file) or CF-migrated patients (migration_summary
        // populated from the Settings → Migration tool) have history
        // somewhere; the kiosk shouldn't make them re-fill the entire
        // intake stack just because the new Cervica DB doesn't have
        // a form_submissions row for them yet.
        // "New" = no completed forms on file and not CF-migrated. The old
        // test was !chart_number — but since the chart-number invariant
        // (June 2026) EVERY patient gets a chart number at insert, so the
        // kiosk treated brand-new patients as established and skipped
        // their entire intake stack (Anna 2026-07-13: NP signed in and it
        // said "all done").
        const isNewPatient = !match.is_migrated && completedTemplateIds.size === 0;
        // Always auto-select staff-pushed assignments (kiosk/in_office
        // channel) regardless of whether the patient is new — those
        // are explicitly required by the biller, not just template
        // defaults. Read from freshTpls (the decorated list) — the
        // `templates` state is stale inside this closure.
        // A staff-pushed assignment ALWAYS surfaces — the front desk
        // explicitly queued it, and the kiosk second-guessing them with
        // "already completed" is how Anna ended up printing paper forms.
        // If it truly is redundant, staff cancel the assignment.
        const staffPushed = freshTpls
          .filter((t) => t.required_by_assignment)
          .map((t) => t.id);
        if (isNewPatient) {
          const stillNeeded = freshTpls
            .filter((t) => t.is_required && !completedTemplateIds.has(t.id))
            .map((t) => t.id);
          // Merge required + staff-pushed, dedupe.
          setSelectedTemplates([...new Set([...stillNeeded, ...staffPushed])]);
        } else {
          setSelectedTemplates(staffPushed);
        }
        setSelectedOutcomes([]);

        // Established patients with nothing queued skip the forms
        // screen entirely — being shown the full template list with
        // "Required" badges on every visit reads as "you must fill
        // these" (Veronica 2026-07-09). New patients and anyone with a
        // staff-pushed form still get the forms step.
        const needsFormsStep = isNewPatient || staffPushed.length > 0;
        setFormsStepNeeded(needsFormsStep);

        // Medicare patients with no active ABN need to sign one before
        // proceeding. Quietly fetch the status — if not_required (non-
        // Medicare) or active, skip the ABN step entirely. If missing
        // or expiring, route to the ABN step.
        let nextStep = needsFormsStep ? STEPS.SELECT_FORMS : STEPS.DONE;
        try {
          const today = new Date().toISOString().slice(0, 10);
          const statusR = await api.get(`/abn/patient/${match.id}/status`, { params: { dos: today } });
          if (statusR.data?.state === 'missing' || statusR.data?.state === 'expiring') {
            nextStep = STEPS.ABN;
            // Reset ABN sub-state in case the patient ran through
            // the kiosk previously without restarting
            setAbnOption('1');
            setAbnSignatureData(null);
            setAbnError('');
          }
        } catch {
          // Non-blocking: if status call fails, proceed to forms.
          // Front desk catches missing ABNs via the schedule badge.
        }

        // Also honor an explicit staff-pushed in-office ABN request. This is
        // the "Send ABN -> In-office iPad" path and works for ANY payer
        // (including Medicare Advantage) because it's a deliberate request,
        // not the Medicare-gated auto-prompt above.
        setAbnRequest(null);
        if (nextStep !== STEPS.ABN) {
          try {
            const rq = await api.get(`/abn/patient/${match.id}/sign-requests`);
            const pending = (rq.data?.requests || []).find(
              (r) => r.status === 'pending' && r.sent_channel === 'in_office',
            );
            if (pending) {
              setAbnRequest(pending);
              nextStep = STEPS.ABN;
              setAbnOption('1');
              setAbnSignatureData(null);
              setAbnError('');
            }
          } catch { /* non-blocking */ }
        }

        // Insurance card freshness check. If we route to ABN, the
        // ABN-submit handler will chain to INS_CARD if needed.
        // Otherwise check now — including when the forms step is
        // being skipped (an established patient with a stale card
        // still needs to snap it before "all done").
        if (nextStep !== STEPS.ABN) {
          if (await needsInsuranceCard(match)) {
            nextStep = STEPS.INS_CARD;
            setCardFront(null);
            setCardBack(null);
            setCardError('');
          }
        }
        const marker = readPendingMarker(match.id);
        if (marker) {
          pendingMarker.current = marker;
          const draft = loadKioskDraft(match.id);
          pendingSubmission.current = draft?.pendingSubmission?.operation_id === marker.operation_id ? draft.pendingSubmission : null;
          setSubmissionRecovery(true);
          setFormQueue([{type:'recovery',data:{id:marker.operation_id,name:'Pending submission'}}]);
          nextStep = STEPS.FILL;
        }
        setStep(nextStep);
      } else {
        setLookupError('We couldn\'t find your record. Please check your information or ask the front desk for help.');
      }
    } catch (err) {
      // Surface the actual error so the front desk can act on it instead
      // of seeing a generic "something went wrong" message. Most common
      // causes when this trips:
      //   401 = receptionist's session expired → log into the kiosk again
      //   429 = rate limit hit → wait 1 minute and retry
      //   500 = server-side error → check ECS logs
      //   network error = ALB/CloudFront blip → retry in 30 sec
      console.error('[Kiosk] Patient lookup failed:', err);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.error;
      let userMsg = 'Something went wrong. Please ask the front desk for help.';
      if (status === 401) {
        userMsg = 'Session expired. Front desk: log back into the kiosk to refresh the session.';
      } else if (status === 429) {
        userMsg = 'Office is busy — please wait 1 minute and try again.';
      } else if (status === 500) {
        userMsg = `Server error — front desk should report this. ${serverMsg ? `(${serverMsg})` : ''}`;
      } else if (!status) {
        userMsg = 'Connection problem — please ask the front desk to check the internet.';
      } else if (serverMsg) {
        userMsg = `Error: ${serverMsg}`;
      }
      setLookupError(userMsg);
    } finally {
      setLookupLoading(false);
    }
  }

  /**
   * Submit the ABN signature captured on the kiosk. Mirrors what
   * ABNSignatureModal does on the staff side, but uses the kiosk's
   * own axios instance and presents simplified text appropriate
   * for patient self-service.
   *
   * Option 3 ("don't want service") routes to a front-desk message
   * instead of saving — that decision needs human conversation, not
   * a self-service tap.
   */

  /**
   * Should the kiosk ask for insurance card photos? (Veronica 2026-07-09:
   * established patients were re-prompted every 90 days, and CF-imported
   * cards live in the Documents tab under other categories, so the old
   * category-scoped check never saw them.)
   *  - cash-tier patients: never (no insurance expected)
   *  - established patients (chart number / migrated): only if NO card-ish
   *    document exists at all
   *  - new patients: card within 90 days
   */
  async function needsInsuranceCard(patient) {
    try {
      const tier = await api.get(`/patients/${patient.id}/self-pay-tier`).then((r) => r.data).catch(() => null);
      if (tier?.tier) return false;
      const docs = await api.get('/documents', { params: { patient_id: patient.id } });
      const all = docs.data?.documents || [];
      const cardish = all.filter((d) => {
        const cat = String(d.category || '').toLowerCase();
        const name = String(d.file_name || d.name || '').toLowerCase();
        return cat.includes('insurance') || cat.includes('card') || cat === 'id' || cat.includes('identification')
          || /insurance|card|\bid\b|license/.test(name);
      });
      const established = !!(patient.chart_number || patient.is_migrated);
      if (established) return cardish.length === 0;
      const recent = cardish.find((d) => d.created_at && (Date.now() - new Date(d.created_at).getTime()) / 86400000 < 90);
      return !recent;
    } catch {
      return false; // never gate check-in on a flaky call
    }
  }

  async function handleAbnSubmit() {
    setAbnError('');

    // Option 3 (decline) saves like the others — a signed, auditable
    // refusal (Daniel 2026-07-07: the old front-desk dead-end stranded
    // patients mid-check-in). The server flags the front desk so staff
    // get the conversation cue; check-in continues normally.
    if (!abnSignatureData) {
      setAbnError('Please sign in the box above before continuing.');
      return;
    }

    setAbnSubmitting(true);
    try {
      if (abnRequest?.token) {
        // Complete the staff-pushed request, honoring the biller's stored
        // draft (reason / charge / coverage dates) via the same endpoint the
        // emailed sign-link uses.
        await api.post(`/abn/sign/${abnRequest.token}/sign`, {
          signature_data_url: abnSignatureData,
          elected_option: parseInt(abnOption, 10) || 1,
        });
      } else {
        const today = new Date().toISOString().slice(0, 10);
        const appliesUntil = new Date();
        appliesUntil.setFullYear(appliesUntil.getFullYear() + 1);
        await api.post(`/abn/patient/${selectedPatient.id}`, {
          scope: 'episode',
          applies_from: today,
          applies_until: appliesUntil.toISOString().slice(0, 10),
          expected_denial_reason:
            abnOption === '1'
              ? 'Patient elected Option 1: services to be billed to Medicare; patient liable if denied.'
              : abnOption === '2'
                ? 'Patient elected Option 2: services not billed to Medicare; patient liable.'
                : 'Patient elected Option 3: declined the service.',
          estimated_charge: null,
          signature_data_url: abnSignatureData,
          notes: `Signed via kiosk self-check-in. CMS-R-131 Box G option ${abnOption}.`,
          elected_option: parseInt(abnOption, 10) || 1,
        });
      }

      // After ABN, check insurance-card freshness same as the
      // post-verify flow. Route to INS_CARD if no recent card. When
      // the forms step isn't needed (established patient, nothing
      // queued) land on Done instead of the form list.
      const afterAbn = formsStepNeeded ? STEPS.SELECT_FORMS : STEPS.DONE;
      try {
        setStep((await needsInsuranceCard(selectedPatient)) ? STEPS.INS_CARD : afterAbn);
      } catch {
        setStep(afterAbn);
      }
    } catch (err) {
      setAbnError(err.response?.data?.error || 'Could not save signature. Please ask the front desk for help.');
    } finally {
      setAbnSubmitting(false);
    }
  }

  /**
   * Submit captured insurance card photos.
   *
   * Flow:
   *   1. Upload front + back to /documents (category=insurance_card).
   *   2. Run Claude Vision OCR on the front image.
   *   3. If OCR produced usable fields (payer_name + member_id), create
   *      a patient_insurance row flagged pending_review=true so the
   *      biller can verify before any claim gets built off it.
   *   4. Continue to the next step regardless of OCR success — patient
   *      should never see check-in fail because the AI couldn't read
   *      their card. Front desk catches the gap on the schedule view.
   *
   * Patient can skip with "I don't have my card today" — the lack of a
   * card surfaces in the schedule badge for front desk.
   */
  async function handleCardSubmit() {
    setCardError('');
    const afterCard = formsStepNeeded ? STEPS.SELECT_FORMS : STEPS.DONE;
    if (!cardFront && !cardBack) {
      setStep(afterCard);
      return;
    }
    setCardSubmitting(true);
    try {
      let frontDocId = null;
      if (cardFront) {
        const fd = new FormData();
        fd.append('file', cardFront, 'insurance_card_front.jpg');
        fd.append('patient_id', selectedPatient.id);
        fd.append('name', `Insurance Card (front) — kiosk ${new Date().toISOString().slice(0, 10)}`);
        fd.append('category', 'insurance_card');
        fd.append('notes', 'Captured via kiosk self-check-in. Front of card.');
        const r = await api.post('/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        frontDocId = r.data?.document?.id || null;
      }
      if (cardBack) {
        const fd = new FormData();
        fd.append('file', cardBack, 'insurance_card_back.jpg');
        fd.append('patient_id', selectedPatient.id);
        fd.append('name', `Insurance Card (back) — kiosk ${new Date().toISOString().slice(0, 10)}`);
        fd.append('category', 'insurance_card');
        fd.append('notes', 'Captured via kiosk self-check-in. Back of card.');
        await api.post('/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }

      // OCR the front and try to auto-create an insurance row. All
      // errors are swallowed — auto-fill is a nice-to-have, not a
      // gating step. Worst case the biller re-keys the card.
      if (cardFront) {
        try {
          const aiFd = new FormData();
          aiFd.append('image', cardFront, 'insurance_card_front.jpg');
          const aiR = await api.post('/ai/parse-insurance-card', aiFd, { headers: { 'Content-Type': 'multipart/form-data' } });
          const ex = aiR.data?.insurance_data || {};
          // Require a payer_name AND member_id before writing a row —
          // anything less is too thin for the biller to act on, and
          // we'd rather show nothing than show a row that's mostly
          // empty (e.g., when the patient handed over a driver's
          // license instead of an insurance card).
          if (ex.payer_name && ex.member_id) {
            await api.post(`/patients/${selectedPatient.id}/insurance`, {
              priority: 'primary',
              payer_name: ex.payer_name,
              payer_id: ex.payer_id || null,
              member_id: ex.member_id,
              group_number: ex.group_number || null,
              plan_name: ex.plan_name || null,
              subscriber_name: ex.subscriber_name || null,
              copay: ex.copay || null,
              deductible: ex.deductible || null,
              out_of_pocket_max: ex.out_of_pocket_max || null,
              effective_date: ex.effective_date || null,
              pending_review: true,
              source: 'kiosk_scan',
              document_id: frontDocId,
            });
          }
        } catch (e) {
          // Log only — don't show the patient an error about a feature
          // they didn't ask for.
          console.warn('[Kiosk] Insurance OCR auto-create failed:', e?.response?.data?.error || e?.message);
        }
      }

      setStep(afterCard);
    } catch (err) {
      setCardError(err.response?.data?.error || 'Could not save photos. Please ask the front desk for help.');
    } finally {
      setCardSubmitting(false);
    }
  }

  function startFilling() {
    const queue = [];
    templates.filter((t) => selectedTemplates.includes(t.id)).forEach((t) => {
      queue.push({ type: 'form', data: t });
    });
    outcomeInstruments.filter((i) => selectedOutcomes.includes(i.id)).forEach((i) => {
      queue.push({ type: 'outcome', data: i });
    });

    if (queue.length === 0) return;
    setFormQueue(queue);

    // Restore a saved draft if one exists for this patient and matches
    // the queue we're about to start (same template+outcome IDs in the
    // same order). Mismatched draft = different forms selected this
    // visit; drop it rather than restore stale data.
    const draft = loadKioskDraft(selectedPatient?.id);
    if (draft?.pendingSubmission?.patient_id === selectedPatient?.id) {
      pendingSubmission.current = draft.pendingSubmission;
      setSubmissionRecovery(true);
    } else { pendingSubmission.current = null; setSubmissionRecovery(false); }
    const queueKey = queue.map((q) => `${q.type}:${q.data.id}`).join('|');
    if (draft && draft.queueKey === queueKey && draft.allResponses && hasFilledValue(draft.allResponses)) {
      setAllResponses(draft.allResponses);
      setSignatures(draft.signatures || {});
      setCurrentForm(typeof draft.currentForm === 'number' ? Math.min(draft.currentForm, queue.length - 1) : 0);
    } else {
      setCurrentForm(0);
      setAllResponses({});
      setSignatures({});
    }
    setStep(STEPS.FILL);
  }

  // Autosave the in-progress draft to localStorage whenever any of
  // the fill-mode state changes. Cheap (sync local writes) and the
  // patient never sees it; only fires while actively filling.
  useEffect(() => {
    if (step !== STEPS.FILL || !selectedPatient?.id || formQueue.length === 0) return;
    const queueKey = formQueue.map((q) => `${q.type}:${q.data.id}`).join('|');
    saveKioskDraft(selectedPatient.id, {
      queueKey,
      allResponses,
      signatures,
      currentForm,
      pendingSubmission: pendingSubmission.current,
    });
  }, [step, selectedPatient?.id, formQueue, allResponses, signatures, currentForm]);

  async function handleSubmitAll(overrideSigs) {
    if (submissionLock.current) return;
    if (pendingMarker.current && !pendingSubmission.current) {
      submissionLock.current = true; setSubmitting(true); setSubmissionError('');
      try {
        const marker = pendingMarker.current;
        const {data} = await api.get(`/forms/kiosk-operations/${marker.operation_id}`, {params:{patient_id:marker.patient_id}});
        if (data?.success !== true || data.operation_id !== marker.operation_id || data.patient_id !== marker.patient_id ||
            data.submissions?.length !== marker.form_count || data.administrations?.length !== marker.outcome_count)
          throw new Error('The complete submission receipt was not confirmed.');
        clearPendingMarker(marker.patient_id); clearKioskDraft(marker.patient_id);
        pendingMarker.current = null; setSubmissionRecovery(false); setStep(STEPS.DONE);
      } catch (error) {
        if (error.response?.status === 404) {
          // The server has no record of this operation: the earlier try
          // never reached it (Kenna Weber 2026-10-15, iPad stuck on
          // Submitting, every status check 404, patient blocked for good).
          // Submissions commit in one transaction with the operation row,
          // so 404 means nothing was saved. Release the marker and let the
          // patient submit again; a saved draft restores their answers.
          const pid = pendingMarker.current?.patient_id || selectedPatient?.id;
          clearPendingMarker(pid);
          pendingMarker.current = null; pendingSubmission.current = null;
          setSubmissionRecovery(false);
          setStep(STEPS.SELECT_FORMS);
          setSubmissionError('');
          alert('The earlier submission did not reach the office, so nothing was saved. Please choose the forms and submit them again.');
          return;
        }
        setSubmissionError('This earlier submission still needs confirmation. Ask the front desk to review it; a new submission is blocked to prevent duplicates.');
      } finally { submissionLock.current = false; setSubmitting(false); }
      return;
    }
    // The Review & Sign step passes the applied-signature map directly —
    // React state hasn't flushed yet when it calls us.
    const sigMap = overrideSigs && typeof overrideSigs === 'object' && !overrideSigs.nativeEvent
      ? overrideSigs : signatures;
    // Pre-submit validation — refuse to send a queue where any form
    // has zero filled fields. Catches the iPad-tab-killed-during-fill
    // case before the empty row ever reaches the server. Also surfaces
    // partial completion (patient skipped form 3 of 5) clearly.
    const blankForms = formQueue
      .filter((q) => q.type === 'form')
      .filter((q) => !hasFilledValue(allResponses[q.data.id] || {}));
    if (!pendingSubmission.current && blankForms.length > 0) {
      const names = blankForms.map((q) => q.data.name).join(', ');
      alert(
        `These forms are still blank: ${names}. ` +
        `If the iPad went to sleep, your work may have been lost — please ask the front desk to help reload the forms before submitting.`
      );
      return;
    }

    // A signature-required form without a captured signature must stop
    // HERE — before this check, such forms submitted quietly and only
    // showed up later as SIGNATURE MISSING on the chart (Mary Ann
    // Statler 2026-07-31).
    const unsignedForms = formQueue
      .filter((q) => q.type === 'form' && q.data.requires_signature)
      .filter((q) => !sigMap[q.data.id]?.data);
    if (!pendingSubmission.current && unsignedForms.length > 0) {
      const names = unsignedForms.map((q) => q.data.name).join(', ');
      alert(`These forms still need a signature: ${names}. Please go back and sign in the signature box on each one.`);
      setCurrentForm(Math.max(0, formQueue.indexOf(unsignedForms[0])));
      return;
    }

    submissionLock.current = true;
    setSubmitting(true); setSubmissionError('');
    try {
      pendingSubmission.current ||= createKioskSubmission({ patientId:selectedPatient.id, queue:formQueue, responses:allResponses, signatures:sigMap });
      const operation = pendingSubmission.current;
      setSubmissionRecovery(true);
      if (operation.patient_id !== selectedPatient.id) throw new Error('The pending submission belongs to another patient. Ask the front desk for help.');
      // Save the exact reviewed answers+signature+operation before the request.
      // Never mint a fresh operation after a lost response or device reload.
      const marked = writePendingMarker(operation);
      if (marked) pendingMarker.current = readPendingMarker(selectedPatient.id);
      const saved = marked && saveKioskDraft(selectedPatient.id, {
        queueKey:formQueue.map(q => `${q.type}:${q.data.id}`).join('|'),
        allResponses, signatures:sigMap, currentForm, pendingSubmission:operation,
      });
      if (!saved) {
        // Nothing was sent. A marker left behind here blocked the patient
        // on every later visit to the kiosk.
        clearPendingMarker(selectedPatient.id); pendingMarker.current = null; pendingSubmission.current = null;
        setSubmissionRecovery(false);
        throw new Error('This device could not save submission recovery information. Ask the front desk for help before submitting.');
      }
      setSubmissionRecovery(true);
      const response = await api.post('/forms/kiosk-submit', operation, { timeout:60000 });
      confirmKioskSubmission(response.data, operation);
      pendingSubmission.current = null; pendingMarker.current = null;
      clearPendingMarker(selectedPatient.id);
      setSubmissionRecovery(false);
      clearKioskDraft(selectedPatient.id);
      setStep(STEPS.DONE);
    } catch (error) {
      const status = error.response?.status;
      if (status === 401) {
        // Sign-in is checked before the submission transaction opens, so a
        // 401 saved nothing. Keep the answers on the device and say so; the
        // old path left a disabled button behind the review screen.
        pendingSubmission.current = null; pendingMarker.current = null;
        clearPendingMarker(selectedPatient.id);
        setSubmissionRecovery(false);
        saveKioskDraft(selectedPatient.id, {queueKey:formQueue.map(q => `${q.type}:${q.data.id}`).join('|'),allResponses,signatures:sigMap,currentForm});
        setSubmissionError(lang === 'es'
          ? 'La sesión de este iPad expiró y no se envió nada. Sus respuestas están guardadas. Pida a la recepción que vuelva a iniciar sesión en el iPad.'
          : "This iPad's sign-in expired, so nothing was sent. Your answers are saved on this iPad. Please ask the front desk to sign the iPad back in.");
        return;
      }
      if (status === 400 || status === 404 || status === 422) {
        // Server validation happens in the transaction: a rejection committed nothing.
        pendingSubmission.current = null; pendingMarker.current = null;
        clearPendingMarker(selectedPatient.id);
        setSubmissionRecovery(false);
        saveKioskDraft(selectedPatient.id, {queueKey:formQueue.map(q => `${q.type}:${q.data.id}`).join('|'),allResponses,signatures:sigMap,currentForm});
        if (formQueue[0]?.type === 'recovery') {
          setStep(STEPS.SELECT_FORMS);
          alert(error.response?.data?.error || 'The server rejected this submission without saving. Review the forms and submit again.');
        }
      }
      setSubmissionError(error.response?.data?.error || error.message || 'Submission could not be confirmed. Retry the same submission.');
    } finally {
      submissionLock.current = false;
      setSubmitting(false);
    }
  }

  function resetKiosk() {
    if (submissionLock.current || pendingSubmission.current || pendingMarker.current) { setSubmissionError('Confirm the pending submission or ask the front desk for help before resetting.'); return; }
    setSubmissionError('');
    setStep(STEPS.SEARCH);
    setSelectedPatient(null);
    setLastName('');
    setDob('');
    setDobRaw('');
    setLookupError('');
    setSelectedTemplates([]);
    setSelectedOutcomes([]);
    setFormQueue([]);
    setCurrentForm(0);
    setAllResponses({});
    setSignatures({});
    setAbnOption('1');
    setAbnSignatureData(null);
    setAbnError('');
    setCardFront(null);
    setCardBack(null);
    setCardError('');
    setFormsStepNeeded(true);
  }

  // ============================================================
  // RENDER
  // ============================================================

  // Login screen
  if (!kioskSetup) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-teal-50 to-white p-6">
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-100">
              <Stethoscope className="h-7 w-7 text-teal-600" />
            </div>
            <h1 className="mt-3 text-[20px] font-bold text-gray-900">Kiosk Setup</h1>
            <p className="text-[13px] text-gray-500">Sign in with a staff account to activate this tablet.</p>
          </div>
          {loginError && <div className="mb-3 rounded-xl bg-red-50 border border-red-200 p-3 text-[13px] text-red-700">{loginError}</div>}
          <input type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="Staff email"
            className="mb-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none" />
          <input type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} placeholder="Password"
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            className="mb-4 w-full rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none" />
          <button onClick={handleLogin} disabled={loginLoading}
            className="w-full rounded-xl bg-teal-600 py-3 text-[15px] font-bold text-white hover:bg-teal-700 disabled:opacity-50">
            {loginLoading ? 'Signing in...' : 'Activate Kiosk'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white">
      {/* Header */}
      <div className="bg-[#0f3d3a] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Stethoscope className="h-5 w-5 text-teal-300" />
          <span className="text-[16px] font-bold text-white">{practiceName}</span>
        </div>
        <div className="flex items-center gap-3">
          {/* Language selector — visible at every step of the kiosk so a
              Spanish-speaking patient can switch at any time, even
              mid-intake. */}
          <div className="flex items-center gap-1 rounded-md bg-white/10 px-1.5 py-0.5">
            <Globe className="h-3.5 w-3.5 text-teal-300" />
            <button
              onClick={() => setLang('en')}
              className={`rounded px-2 py-0.5 text-[12px] font-medium ${lang === 'en' ? 'bg-white text-teal-800' : 'text-teal-200 hover:text-white'}`}
            >
              English
            </button>
            <button
              onClick={() => setLang('es')}
              className={`rounded px-2 py-0.5 text-[12px] font-medium ${lang === 'es' ? 'bg-white text-teal-800' : 'text-teal-200 hover:text-white'}`}
            >
              Español
            </button>
          </div>
          {step !== STEPS.SEARCH && step !== STEPS.DONE && (
            <button onClick={resetKiosk} className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-[12px] text-teal-200 hover:bg-white/10">
              <RotateCcw className="h-3.5 w-3.5" /> {lang === 'es' ? 'Comenzar de nuevo' : 'Start Over'}
            </button>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6">
        {/* STEP 1: Patient Verification (HIPAA-safe — no patient list shown) */}
        {step === STEPS.SEARCH && (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <h2 className="text-[22px] font-bold text-gray-900">Welcome!</h2>
              <p className="text-[15px] text-gray-500 mt-1">Please verify your identity to get started.</p>
            </div>

            <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-900/[0.04] space-y-4">
              {lookupError && (
                <div className="flex items-center gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-[14px] text-red-700">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-500" />
                  {lookupError}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-[14px] font-semibold text-gray-700">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter your last name"
                  autoFocus
                  autoComplete="off"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3.5 text-[18px] focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-[14px] font-semibold text-gray-700">Date of Birth</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={dob}
                  onChange={handleDobInput}
                  placeholder="MM/DD/YYYY"
                  autoComplete="off"
                  maxLength={10}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3.5 text-[18px] focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                />
              </div>

              <button
                onClick={handlePatientLookup}
                disabled={!lastName.trim() || dobRaw.length < 8 || lookupLoading}
                className="w-full rounded-xl bg-teal-600 py-3.5 text-[16px] font-bold text-white shadow-sm hover:bg-teal-700 disabled:opacity-40"
              >
                {lookupLoading ? 'Verifying...' : 'Check In'}
              </button>
            </div>

            <p className="text-center text-[13px] text-gray-400">
              Can't check in? Please ask the front desk for assistance.
            </p>
          </div>
        )}

        {/* STEP 1.5: ABN signature gate (Medicare patients only).
            CMS-R-131 in patient-readable language; same data shape
            and 1-year episode default as the staff modal. */}
        {step === STEPS.ABN && selectedPatient && (
          <div className="space-y-4">
            <div className="text-center mb-2">
              <h2 className="text-[20px] font-bold text-gray-900">One quick form, {selectedPatient.first_name}</h2>
              <p className="text-[13px] text-gray-500 mt-1">Please read and sign before we get started.</p>
            </div>

            <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-900/[0.04] space-y-4">
              <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-[14px] text-amber-900 leading-relaxed">
                <p className="font-bold mb-1">Notice about your Medicare coverage</p>
                <p>
                  Medicare covers spinal manipulation by a chiropractor, but it does <strong>not</strong> cover
                  other services your doctor may provide today — such as manual therapy, mechanical traction,
                  electrical stimulation, exams, or x-rays. Medicare considers these services "non-covered"
                  when a chiropractor performs them.
                </p>
                <p className="mt-2">
                  You may still receive these services. If you choose to, you'll be responsible for the cost.
                  Please pick an option below, then sign.
                </p>
              </div>

              <div>
                <p className="block text-[13px] font-bold text-gray-700 mb-2">Choose one option:</p>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 cursor-pointer rounded-xl border-2 px-4 py-3 transition-colors hover:bg-gray-50 has-checked:border-teal-500 has-checked:bg-teal-50">
                    <input type="radio" checked={abnOption === '1'} onChange={() => setAbnOption('1')}
                      className="mt-1 h-4 w-4 text-teal-600" />
                    <div className="text-[13px] text-gray-800">
                      <strong>Option 1.</strong> I want the service. Please bill Medicare so I can get an
                      official decision. If Medicare doesn't pay, I will pay.
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer rounded-xl border-2 px-4 py-3 transition-colors hover:bg-gray-50 has-checked:border-teal-500 has-checked:bg-teal-50">
                    <input type="radio" checked={abnOption === '2'} onChange={() => setAbnOption('2')}
                      className="mt-1 h-4 w-4 text-teal-600" />
                    <div className="text-[13px] text-gray-800">
                      <strong>Option 2.</strong> I want the service. Please don't bill Medicare. I will pay
                      now and I understand I can't appeal.
                    </div>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer rounded-xl border-2 px-4 py-3 transition-colors hover:bg-gray-50 has-checked:border-teal-500 has-checked:bg-teal-50">
                    <input type="radio" checked={abnOption === '3'} onChange={() => setAbnOption('3')}
                      className="mt-1 h-4 w-4 text-teal-600" />
                    <div className="text-[13px] text-gray-800">
                      <strong>Option 3.</strong> I don't want the service. I won't be billed for it.
                    </div>
                  </label>
                </div>
              </div>

              <div>
                <p className="block text-[13px] font-bold text-gray-700 mb-1">Your signature</p>
                <SignaturePad
                  onSign={(data) => setAbnSignatureData(data)}
                  signerName={`${selectedPatient.first_name} ${selectedPatient.last_name}`}
                />
                <p className="mt-1 text-[11px] text-gray-500">
                  By signing, you confirm you've read and understood the notice above.
                </p>
              </div>

              {abnError && (
                <div className="flex items-center gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 text-red-500" />
                  {abnError}
                </div>
              )}

              <button
                onClick={handleAbnSubmit}
                disabled={abnSubmitting}
                className="w-full rounded-xl bg-teal-600 py-3.5 text-[15px] font-bold text-white shadow-sm hover:bg-teal-700 disabled:opacity-40"
              >
                {abnSubmitting ? 'Saving...' : abnOption === '3' ? 'Continue' : 'Sign and continue'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 1.7: Insurance card capture (when patient has no
            recent card on file). Patient can skip; front desk sees
            the gap on the schedule badge. */}
        {step === STEPS.INS_CARD && selectedPatient && (
          <div className="space-y-4">
            <div className="text-center mb-2">
              <h2 className="text-[20px] font-bold text-gray-900">Insurance card photo</h2>
              <p className="text-[13px] text-gray-500 mt-1">
                We don't have a recent photo of your insurance card. A quick photo of the front and back helps
                us bill correctly and avoid surprise charges.
              </p>
            </div>

            <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-900/[0.04] space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <CardSlot
                  label="Front of card"
                  file={cardFront}
                  onCapture={(f) => setCardFront(f)}
                  onClear={() => setCardFront(null)}
                />
                <CardSlot
                  label="Back of card"
                  file={cardBack}
                  onCapture={(f) => setCardBack(f)}
                  onClear={() => setCardBack(null)}
                />
              </div>

              {cardError && (
                <div className="flex items-center gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 text-red-500" />
                  {cardError}
                </div>
              )}

              <button
                onClick={handleCardSubmit}
                disabled={cardSubmitting}
                className="w-full rounded-xl bg-teal-600 py-3.5 text-[15px] font-bold text-white shadow-sm hover:bg-teal-700 disabled:opacity-40">
                {cardSubmitting ? 'Saving...' : (cardFront || cardBack) ? 'Save and continue' : 'Skip — I don\'t have my card today'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Select Forms */}
        {step === STEPS.SELECT_FORMS && selectedPatient && (() => {
          // Hide templates the patient has already submitted — they
          // don't need to re-fill paperwork they've done before.
          // EXCEPT staff-pushed assignments: a form the front desk
          // explicitly queued must ALWAYS render, even over a completed
          // submission — re-collecting a signature on a "completed"
          // form is exactly why staff queue them (Damaris/Ndeye
          // 2026-08-06: six queued forms invisible on the iPad while
          // "Start (6 selected)" counted the hidden selection).
          // Then sort: forms a biller queued via the chart (`required_by_assignment`)
          // float to the top so the patient sees them first.
          const visibleTemplates = templates
            .filter((t) => t.required_by_assignment || !completedTplIds.has(t.id))
            .slice()
            .sort((a, b) => {
              const ar = a.required_by_assignment ? 0 : 1;
              const br = b.required_by_assignment ? 0 : 1;
              if (ar !== br) return ar - br;
              return (a.sort_order || 0) - (b.sort_order || 0);
            });
          const nothingToDo = visibleTemplates.length === 0 && outcomeInstruments.length === 0;

          // Truly nothing left? Show a clean "all set" screen with a
          // single Finish button that records the kiosk check-in and
          // sends the patient to the waiting area.
          if (nothingToDo) {
            return (
              <div className="space-y-6 text-center py-8">
                <div className="flex justify-center">
                  <div className="h-20 w-20 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="h-10 w-10 text-emerald-600" />
                  </div>
                </div>
                <div>
                  <h2 className="text-[24px] font-bold text-gray-900">You're all set, {selectedPatient.first_name}!</h2>
                  <p className="mt-2 text-[14px] text-gray-600">Your paperwork is up to date. Please have a seat — someone will be with you shortly.</p>
                </div>
                <button onClick={() => setStep(STEPS.DONE)}
                  className="rounded-xl bg-teal-600 px-8 py-3 text-[15px] font-bold text-white shadow-sm hover:bg-teal-700">
                  Done
                </button>
              </div>
            );
          }

          return (
          <div className="space-y-4">
            <div className="text-center mb-2">
              <h2 className="text-[20px] font-bold text-gray-900">Hi, {selectedPatient.first_name}!</h2>
              <p className="text-[14px] text-gray-500">
                {selectedTemplates.length > 0
                  ? 'Please complete the selected forms below.'
                  : 'Optional forms are available. Pick any to fill, or tap Done to skip.'}
              </p>
            </div>

            {/* Form templates — only templates the patient hasn't done yet */}
            {visibleTemplates.length > 0 && (
              <div>
                <h3 className="mb-2 text-[13px] font-bold text-gray-700 uppercase tracking-wider">Forms & Consent</h3>
                <div className="space-y-2">
                  {visibleTemplates.map((t) => {
                    const selected = selectedTemplates.includes(t.id);
                    // Staff-queued forms are highlighted with a rose
                    // border + "Sent by staff" badge so the patient
                    // notices them. Also auto-included when present
                    // (selectedTemplates seeded with these earlier).
                    const isAssigned = !!t.required_by_assignment;
                    return (
                      <button key={t.id} onClick={() => {
                        if (selected) setSelectedTemplates(selectedTemplates.filter((id) => id !== t.id));
                        else setSelectedTemplates([...selectedTemplates, t.id]);
                      }}
                        className={`flex w-full items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                          isAssigned && !selected ? 'border-rose-300 bg-rose-50/40' :
                          selected ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white'
                        }`}>
                        <div className={`flex h-6 w-6 items-center justify-center rounded-lg border-2 ${selected ? 'border-teal-500 bg-teal-500 text-white' : 'border-gray-300'}`}>
                          {selected && <CheckCircle className="h-4 w-4" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-[14px] font-medium text-gray-900">{t.name}</p>
                          {t.description && <p className="text-[12px] text-gray-500">{t.description}</p>}
                          {isAssigned && t.assignment_note && (
                            <p className="mt-1 text-[11px] italic text-rose-700">"{t.assignment_note}"</p>
                          )}
                        </div>
                        {isAssigned ? (
                          <span className="ml-auto rounded-full bg-rose-600 px-2.5 py-0.5 text-[10px] font-bold text-white">Sent by staff</span>
                        ) : t.is_required ? (
                          <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">Required</span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Outcome measures */}
            {outcomeInstruments.length > 0 && (
              <div>
                <h3 className="mb-2 text-[13px] font-bold text-gray-700 uppercase tracking-wider">Outcome Measures</h3>
                <div className="space-y-2">
                  {outcomeInstruments.map((inst) => {
                    const selected = selectedOutcomes.includes(inst.id);
                    return (
                      <button key={inst.id} onClick={() => {
                        if (selected) setSelectedOutcomes(selectedOutcomes.filter((id) => id !== inst.id));
                        else setSelectedOutcomes([...selectedOutcomes, inst.id]);
                      }}
                        className={`flex w-full items-center gap-3 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                          selected ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white'
                        }`}>
                        <div className={`flex h-6 w-6 items-center justify-center rounded-lg border-2 ${selected ? 'border-teal-500 bg-teal-500 text-white' : 'border-gray-300'}`}>
                          {selected && <CheckCircle className="h-4 w-4" />}
                        </div>
                        <div>
                          <p className="text-[14px] font-medium text-gray-900">{inst.name}</p>
                          <p className="text-[12px] text-gray-500">{inst.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4">
              <button onClick={resetKiosk} className="rounded-xl px-5 py-3 text-[14px] font-medium text-gray-600 hover:bg-gray-100">
                <ChevronLeft className="inline h-4 w-4 mr-1" /> Back
              </button>
              {/* If nothing is selected but the patient has reached
                  this step (some optional forms or outcomes available),
                  let them tap Done to bail out instead of being stuck
                  with a disabled Start button. */}
              {selectedTemplates.length === 0 && selectedOutcomes.length === 0 ? (
                <button onClick={() => setStep(STEPS.DONE)}
                  className="rounded-xl bg-teal-600 px-8 py-3 text-[15px] font-bold text-white shadow-sm hover:bg-teal-700">
                  Done
                </button>
              ) : (
                <button onClick={startFilling}
                  className="rounded-xl bg-teal-600 px-8 py-3 text-[15px] font-bold text-white shadow-sm hover:bg-teal-700">
                  Start ({selectedTemplates.length + selectedOutcomes.length} selected)
                </button>
              )}
            </div>
          </div>
          );
        })()}

        {/* STEP 3: Fill Forms */}
        {step === STEPS.FILL && formQueue.length > 0 && (
          <>
          {submissionError && <p role="alert" className="mb-4 rounded-lg bg-amber-50 p-4 text-amber-900">{submissionError}</p>}
          {submissionRecovery && <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4"><p>Your reviewed answers are held unchanged until the submission is confirmed.</p><button type="button" disabled={submitting} onClick={() => handleSubmitAll()} className="mt-3 rounded-lg bg-teal-700 px-4 py-2 font-semibold text-white disabled:opacity-50">{submitting ? 'Confirming…' : pendingSubmission.current ? 'Retry same submission' : 'Check submission status'}</button></div>}
          {formQueue[0]?.type !== 'recovery' && <fieldset disabled={submitting || submissionRecovery}>
          <KioskFormFiller
            queue={formQueue}
            currentIndex={currentForm}
            setCurrentIndex={setCurrentForm}
            allResponses={allResponses}
            setAllResponses={setAllResponses}
            signatures={signatures}
            setSignatures={setSignatures}
            onSubmit={handleSubmitAll}
            submitting={submitting || submissionRecovery}
            submissionError={submissionError}
            patientName={selectedPatient?.first_name}
            patientGender={selectedPatient?.gender}
            lang={lang}
          />
          </fieldset>}</>
        )}

        {/* STEP 4: Done */}
        {step === STEPS.DONE && (
          <div className="text-center py-12">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-teal-100">
              <CheckCircle className="h-10 w-10 text-teal-600" />
            </div>
            <h2 className="text-[24px] font-bold text-gray-900">All Done!</h2>
            <p className="mt-2 text-[16px] text-gray-600">Thank you, {selectedPatient?.first_name}.</p>
            <p className="mt-1 text-[14px] text-gray-400">Please return this device to the front desk.</p>

            <button onClick={resetKiosk}
              className="mt-8 rounded-xl bg-teal-600 px-8 py-3 text-[15px] font-bold text-white shadow-sm hover:bg-teal-700">
              Next Patient
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Form Filler — renders one form/outcome at a time
// ============================================================

/**
 * CardSlot — one tile per side (front/back) for kiosk insurance
 * card capture. Uses native iOS/iPad camera capture via the file
 * input's `capture` attribute. Shows a thumbnail once captured;
 * tap "Retake" to replace.
 */
function CardSlot({ label, file, onCapture, onClear }) {
  const inputRef = useRef(null);
  const preview = file ? URL.createObjectURL(file) : null;
  return (
    <div>
      <p className="mb-1 text-[13px] font-bold text-gray-700">{label}</p>
      {preview ? (
        <div className="relative">
          <img src={preview} alt={label} className="rounded-lg border border-gray-200 max-h-[180px] w-full object-contain bg-black/5" />
          <button onClick={onClear} type="button"
            className="absolute top-1 right-1 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-semibold text-gray-700 hover:bg-white shadow">
            Retake
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()}
          className="flex h-[180px] w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:border-primary-300 hover:bg-primary-50/50 transition-colors">
          <span className="text-[28px]">📷</span>
          <span className="mt-1 text-[12px] font-semibold text-gray-600">Tap to take photo</span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onCapture(f);
          e.target.value = ''; // allow same file re-pick after retake
        }}
        className="hidden"
      />
    </div>
  );
}

function KioskFormFiller({ queue, currentIndex, setCurrentIndex, allResponses, setAllResponses, signatures, setSignatures, onSubmit, submitting, submissionError, patientName, patientGender, lang = 'en' }) {
  // Review & Sign — one drawn signature applied to every acknowledged
  // form (each keeps its own checkbox + its own signature record).
  const [reviewing, setReviewing] = useState(false);
  // The review screen covers the page, so a submission error rendered
  // above the forms was invisible and the patient saw a dead button. Drop
  // back to the forms whenever an error appears so the message (and the
  // retry control) is on screen.
  useEffect(() => { if (submissionError) setReviewing(false); }, [submissionError]);
  const [acks, setAcks] = useState({});
  const [reviewName, setReviewName] = useState(patientName || '');
  const reviewPadRef = useRef(null);
  const item = queue[currentIndex];
  const isForm = item.type === 'form';
  const isOutcome = item.type === 'outcome';
  const id = item.data.id;
  const responses = allResponses[id] || {};
  const isLast = currentIndex === queue.length - 1;

  const fields = isForm
    ? (typeof item.data.fields === 'string' ? JSON.parse(item.data.fields) : (item.data.fields || []))
    : [];
  const questions = isOutcome
    ? (typeof item.data.questions === 'string' ? JSON.parse(item.data.questions) : (item.data.questions || []))
    : [];

  function updateResponse(fieldId, value) {
    setAllResponses({ ...allResponses, [id]: { ...responses, [fieldId]: value } });
  }

  const progress = ((currentIndex) / queue.length) * 100;

  return (
    <div className="space-y-4">
      {/* Progress */}
      <div>
        <div className="flex gap-1.5 mb-1">
          {queue.map((_, i) => (
            <div key={i} className={`flex-1 h-1.5 rounded-full ${i <= currentIndex ? 'bg-teal-500' : 'bg-gray-200'}`} />
          ))}
        </div>
        <p className="text-[12px] text-gray-500 text-center">
          {currentIndex + 1} of {queue.length}: <strong>{item.data.name}</strong>
        </p>
      </div>

      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-900/[0.04]">
        {item.data.description && (
          <p className="mb-4 text-[14px] text-gray-600">{item.data.description}</p>
        )}

        {/* Form fields — show_if conditionals hide until their
            controlling question is answered (intake trim 2026-07-09) */}
        {isForm && (
          <div className="space-y-5">
            {fields.filter((field) => !isCeremonyField(field, item.data) && isFieldVisible(field, responses, patientGender)).map((field) => (
              <IntakeField key={field.id} field={field} value={responses[field.id]} onChange={(v) => updateResponse(field.id, v)} lang={lang} />
            ))}
            {item.data.requires_signature && (
              <div className="border-t border-gray-200 pt-4 mt-4">
                <p className="rounded-xl bg-teal-50 px-4 py-3 text-[13px] text-teal-800">
                  {lang === 'es'
                    ? 'Firmará este formulario en el paso final de "Revisar y firmar".'
                    : 'You will sign this form on the final "Review & sign" step.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Outcome measure questions */}
        {isOutcome && (
          <div className="space-y-3">
            {questions.map((q) => (
              <div key={q.id} className="flex items-center gap-2 rounded-xl border border-gray-100 px-3 py-2">
                <p className="flex-1 text-[14px] font-medium text-gray-800 min-w-[160px]">{q.text}</p>
                <div className="flex gap-1 flex-shrink-0">
                  {(q.options || []).map((opt) => (
                    <button key={opt.value} onClick={() => updateResponse(q.id, opt.value)}
                      className={`rounded-lg px-3 py-2 text-[12px] font-semibold transition-all ${
                        responses[q.id] === opt.value
                          ? 'bg-teal-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}>
                      {opt.label.length > 20 ? opt.value : opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        <button onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))} disabled={currentIndex === 0}
          className="flex items-center gap-1.5 rounded-xl px-5 py-3 text-[14px] font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-30">
          <ChevronLeft className="h-4 w-4" /> Back
        </button>

        {isLast ? (
          <button onClick={() => setReviewing(true)} disabled={submitting}
            className="flex items-center gap-2 rounded-xl bg-teal-600 px-8 py-3 text-[15px] font-bold text-white shadow-sm hover:bg-teal-700 disabled:opacity-50">
            {lang === 'es' ? 'Revisar y firmar' : 'Review & Sign'}
            <ChevronRight className="h-4 w-4" />
          </button>
        ) : (
          <button onClick={() => setCurrentIndex(currentIndex + 1)}
            className="flex items-center gap-1.5 rounded-xl bg-teal-600 px-6 py-3 text-[15px] font-bold text-white shadow-sm hover:bg-teal-700">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Review & Sign overlay — one signature, applied to each
          acknowledged form's own signature record. */}
      {reviewing && (() => {
        const sigForms = queue.filter((q) => q.type === 'form' && q.data.requires_signature);
        const allAcked = sigForms.every((q) => acks[q.data.id]);
        const canSubmit = allAcked && reviewName.trim().length >= 2 && !submitting;
        return (
          <div className="fixed inset-0 z-40 overflow-y-auto bg-gray-50 px-5 py-8">
            <div className="mx-auto max-w-xl">
              <h2 className="text-[20px] font-bold text-gray-900">
                {lang === 'es' ? 'Revisar y firmar' : 'Review & Sign'}
              </h2>
              <p className="mt-1 text-[13px] text-gray-600">
                {lang === 'es'
                  ? 'Marque cada documento para confirmar que lo leyó y está de acuerdo. Su firma se aplicará a cada documento marcado.'
                  : 'Check each document to confirm you have read and agree to it. Your signature below will be applied to each checked document.'}
              </p>
              <div className="mt-4 space-y-2">
                {sigForms.map((q) => (
                  <label key={q.data.id} className={`flex items-start gap-3 rounded-xl border-2 p-3.5 ${
                    acks[q.data.id] ? 'border-teal-300 bg-teal-50/60' : 'border-gray-200 bg-white'
                  }`}>
                    <input type="checkbox" checked={!!acks[q.data.id]}
                      onChange={(e) => setAcks({ ...acks, [q.data.id]: e.target.checked })}
                      className="mt-0.5 h-6 w-6 rounded border-gray-300 text-teal-600" />
                    <span className="flex-1">
                      <span className="block text-[15px] font-semibold text-gray-800">{q.data.name}</span>
                      <span className="block text-[12px] text-gray-500">
                        {lang === 'es' ? 'He leído y estoy de acuerdo con este documento.' : 'I have read and agree to this document.'}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              <div className="mt-4 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-gray-900/[0.04]">
                <SignaturePad
                  ref={reviewPadRef}
                  signerName={reviewName}
                  onNameChange={setReviewName}
                  onSign={() => {}}
                />
              </div>
              <div className="mt-4 flex items-center justify-between">
                <button onClick={() => setReviewing(false)}
                  className="flex items-center gap-1.5 rounded-xl px-5 py-3 text-[14px] font-medium text-gray-600 hover:bg-gray-100">
                  <ChevronLeft className="h-4 w-4" /> {lang === 'es' ? 'Volver' : 'Back to forms'}
                </button>
                <button
                  disabled={!canSubmit}
                  onClick={() => {
                    const padData = reviewPadRef.current?.getData?.();
                    if (!padData) {
                      alert(lang === 'es' ? 'Por favor firme en el recuadro.' : 'Please sign in the signature box.');
                      return;
                    }
                    const finalSigs = { ...signatures };
                    for (const q of sigForms) {
                      finalSigs[q.data.id] = { data: padData, signer_name: reviewName.trim() };
                    }
                    setSignatures(finalSigs);
                    onSubmit(finalSigs);
                  }}
                  className="flex items-center gap-2 rounded-xl bg-teal-600 px-8 py-3 text-[15px] font-bold text-white shadow-sm hover:bg-teal-700 disabled:opacity-40">
                  {submitting ? (lang === 'es' ? 'Enviando...' : 'Submitting...') : (lang === 'es' ? 'Firmar y enviar' : 'Sign & Submit All')}
                  <CheckCircle className="h-4 w-4" />
                </button>
              </div>
              {!allAcked && (
                <p className="mt-2 text-center text-[12px] text-gray-500">
                  {lang === 'es' ? 'Marque todos los documentos para continuar.' : 'Check every document above to enable submitting.'}
                </p>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ============================================================
// Reused from IntakePage — touch-friendly field renderer
// ============================================================

function IntakeField({ field, value, onChange, lang = 'en' }) {
  const label = t(field.label, lang);
  const options = field.options ? tOptions(field.options, lang) : [];
  const originalOptions = field.options || []; // store values in English so backend isn't language-dependent

  switch (field.type) {
    case 'section':
      return <h2 className="text-[16px] font-bold text-gray-900 pt-3 border-t border-gray-100">{label}</h2>;
    case 'static_text':
      return (
        <div>
          <h3 className="text-[14px] font-semibold text-gray-900 mb-2">{label}</h3>
          <div className="rounded-xl bg-gray-50 p-4 text-[13px] text-gray-600 leading-relaxed whitespace-pre-line max-h-[200px] overflow-y-auto">{field.content}</div>
        </div>
      );
    case 'textarea':
      return (
        <div>
          <FieldLabel label={field.label} required={field.required} lang={lang} />
          <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={field.placeholder}
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20" />
        </div>
      );
    case 'select':
      return (
        <div>
          <FieldLabel label={field.label} required={field.required} lang={lang} />
          <select value={value || ''} onChange={(e) => onChange(e.target.value)}
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none">
            <option value="">{tUI('selectOption', lang)}</option>
            {originalOptions.map((o, i) => <option key={o} value={o}>{options[i] || o}</option>)}
          </select>
        </div>
      );
    case 'radio':
      return (
        <div>
          <FieldLabel label={field.label} required={field.required} lang={lang} />
          <div className="flex flex-wrap gap-2">
            {originalOptions.map((o, i) => (
              <button key={o} type="button" onClick={() => onChange(o)}
                className={`rounded-xl border-2 px-5 py-2.5 text-[14px] font-medium transition-all ${value === o ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-600'}`}>
                {options[i] || o}
              </button>
            ))}
          </div>
        </div>
      );
    case 'checkbox':
      return (
        <label className="flex items-start gap-3 rounded-xl border border-gray-200 p-4 cursor-pointer hover:bg-gray-50">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-5 w-5 rounded border-gray-300 text-teal-600" />
          <span className="text-[14px] text-gray-700 leading-relaxed">{label} {field.required && <span className="text-red-500">*</span>}</span>
        </label>
      );
    case 'checkbox_group':
      return (
        <div>
          <FieldLabel label={field.label} required={field.required} lang={lang} />
          <div className="grid grid-cols-2 gap-2">
            {originalOptions.map((o, i) => {
              const sel = Array.isArray(value) && value.includes(o);
              return (
                <label key={o} className={`flex items-center gap-2.5 rounded-xl border-2 px-4 py-2.5 cursor-pointer transition-all ${sel ? 'border-teal-500 bg-teal-50' : 'border-gray-200'}`}>
                  <input type="checkbox" checked={sel} onChange={(e) => {
                    const arr = Array.isArray(value) ? [...value] : [];
                    if (e.target.checked) arr.push(o); else arr.splice(arr.indexOf(o), 1);
                    onChange(arr);
                  }} className="h-4 w-4 rounded border-gray-300 text-teal-600" />
                  <span className="text-[13px] text-gray-700">{options[i] || o}</span>
                </label>
              );
            })}
          </div>
        </div>
      );
    case 'pain_scale':
      return (
        <div>
          <FieldLabel label={field.label} required={field.required} lang={lang} />
          <div className="flex gap-1.5 justify-center">
            {Array.from({ length: 11 }).map((_, i) => (
              <button key={i} type="button" onClick={() => onChange(i)}
                className={`flex h-12 w-12 items-center justify-center rounded-xl border-2 text-[15px] font-bold transition-all ${
                  value === i ? (i <= 1 ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : i <= 3 ? 'border-yellow-500 bg-yellow-50 text-yellow-700' : i <= 6 ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-red-500 bg-red-50 text-red-700') : 'border-gray-200 text-gray-500'
                }`}>{i}</button>
            ))}
          </div>
          <div className="flex justify-between mt-1 text-[11px] text-gray-400 px-1">
            <span>{tUI('noPain', lang)}</span><span>{tUI('worstPain', lang)}</span>
          </div>
        </div>
      );
    case 'address':
      return (
        <div>
          <FieldLabel label={field.label} required={field.required} lang={lang} />
          <div className="space-y-2">
            <input type="text" value={value?.street || ''} onChange={(e) => onChange({ ...(value || {}), street: e.target.value })} placeholder={tUI('streetAddress', lang)}
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none" />
            <div className="grid grid-cols-3 gap-2">
              <input type="text" value={value?.city || ''} onChange={(e) => onChange({ ...(value || {}), city: e.target.value })} placeholder={tUI('city', lang)} className="rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none" />
              <input type="text" value={value?.state || ''} onChange={(e) => onChange({ ...(value || {}), state: e.target.value })} placeholder={tUI('state', lang)} maxLength={2} className="rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none" />
              <input type="text" value={value?.zip || ''} onChange={(e) => onChange({ ...(value || {}), zip: e.target.value })} placeholder={tUI('zip', lang)} className="rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none" />
            </div>
          </div>
        </div>
      );
    case 'photo':
      return (
        <div>
          <FieldLabel label={field.label} required={field.required} lang={lang} />
          {value ? (
            <div className="relative">
              <img src={value} alt={label} className="w-full rounded-xl border border-gray-200" />
              <button onClick={() => onChange(null)} className="absolute top-2 right-2 rounded-full bg-red-500 p-1.5 text-white shadow-lg">
                <span className="text-[12px] font-bold">✕</span>
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              <label className="flex flex-1 cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-gray-300 p-6 text-gray-500 hover:border-teal-400 hover:text-teal-600">
                <span className="text-[24px]">📸</span>
                <span className="text-[13px] font-medium">{tUI('takePhoto', lang)}</span>
                <input type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(e) => { const f = e.target.files[0]; if (f) { const r = new FileReader(); r.onload = (ev) => onChange(ev.target.result); r.readAsDataURL(f); } }} />
              </label>
              <label className="flex flex-1 cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-gray-300 p-6 text-gray-500 hover:border-teal-400 hover:text-teal-600">
                <span className="text-[24px]">📁</span>
                <span className="text-[13px] font-medium">{tUI('upload', lang)}</span>
                <input type="file" accept="image/*" className="hidden"
                  onChange={(e) => { const f = e.target.files[0]; if (f) { const r = new FileReader(); r.onload = (ev) => onChange(ev.target.result); r.readAsDataURL(f); } }} />
              </label>
            </div>
          )}
        </div>
      );
    default:
      return (
        <div>
          <FieldLabel label={field.label} required={field.required} lang={lang} />
          <input type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : field.type === 'date' ? 'date' : 'text'}
            value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder}
            className="w-full rounded-xl border border-gray-200 px-4 py-3 text-[15px] focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20" />
        </div>
      );
  }
}

function FieldLabel({ label, required, lang = 'en' }) {
  return <label className="mb-1.5 block text-[14px] font-semibold text-gray-700">{t(label, lang)} {required && <span className="text-red-500">*</span>}</label>;
}
